/**
 * Unsupported language detection for location redirect popup
 * @module location-redirect-popup/unsupported-language
 */

import { SELECTORS, LOCALE_PATTERN, SOURCE_LOCALE_PARAM } from './constants.js';
import { parseStringsFromScript } from './geolocation.js';

export interface UnsupportedLanguageResult {
  detected: boolean;
  sourceLocale: string | null;
}

export interface UnsupportedLanguageStrings extends Record<string, unknown> {
  subtitle: string;
  currency_note: string;
  continue_btn: string;
}

/**
 * @typedef {Object} UnsupportedLanguageResult
 * @property {boolean} detected - Whether an unsupported language redirect was detected
 * @property {string|null} sourceLocale - The original non-English locale (e.g. 'es-us'), or null
 */

/**
 * Parses sister site domains from the brand switcher navigation in the header.
 * Finds all links inside the nav, extracts hostnames, and filters out the current site.
 * @returns {string[]} Array of sister site hostnames (e.g. 'theordinary.com', 'qa.niod.com')
 */
export function getSisterSiteDomains(): string[] {
  const nav = document.querySelector<HTMLElement>(SELECTORS.brandSwitcherNav);
  if (!nav) return [];

  const currentHostname = window.location.hostname;
  const links = nav.querySelectorAll<HTMLAnchorElement>('a[href]');
  const domains: string[] = [];
  links.forEach((link) => {
    try {
      const linkUrl = new URL(link.href);
      const linkHostname = linkUrl.hostname;
      if (linkHostname && linkHostname !== currentHostname && !domains.includes(linkHostname)) {
        domains.push(linkHostname);
      }
    } catch {
      // Skip relative or invalid URLs
    }
  });
  return domains;
}

/**
 * Extracts the language portion from a locale string and checks if it is non-English.
 * @param {string} locale - Locale string (e.g. 'es-us', 'fr-ca')
 * @returns {boolean} True if the language is not English
 */
function isNonEnglishLocale(locale: string): boolean {
  const language = locale.split('-')[0].toLowerCase();
  return language !== 'en';
}

/**
 * Detects if the user was redirected from a non-English locale.
 *
 * Primary signal: the `source_locale` query parameter set by the server-side
 * 301 redirect (e.g. /es-us/ → /en-us/?source_locale=es-us).
 *
 * Fallback: `document.referrer` pointing to a sister site with a non-English
 * locale path (covers cases where the redirect param is absent).
 *
 * @returns {UnsupportedLanguageResult}
 */
export function detectUnsupportedLanguageRedirect(): UnsupportedLanguageResult {
  // Primary: check source_locale query param from server-side redirect
  const searchParams = new URLSearchParams(window.location.search);
  const sourceLocale = searchParams.get(SOURCE_LOCALE_PARAM);

  if (sourceLocale && isNonEnglishLocale(sourceLocale)) {
    return { detected: true, sourceLocale: sourceLocale };
  }

  // Fallback: check document.referrer for sister site with non-English locale
  const referrer = document.referrer;
  if (!referrer) return { detected: false, sourceLocale: null };

  try {
    const url = new URL(referrer);
    const hostname = url.hostname;

    const sisterSiteDomains = getSisterSiteDomains();
    const isSisterSite = sisterSiteDomains.some((domain) => {
      return hostname === domain || hostname.endsWith('.' + domain);
    });
    if (!isSisterSite) return { detected: false, sourceLocale: null };

    const localeMatch = url.pathname.match(LOCALE_PATTERN);
    if (!localeMatch) return { detected: false, sourceLocale: null };

    const locale = localeMatch[0].substring(1); // Remove leading /
    if (!isNonEnglishLocale(locale)) return { detected: false, sourceLocale: null };

    return { detected: true, sourceLocale: locale };
  } catch {
    return { detected: false, sourceLocale: null };
  }
}

/**
 * Removes the source_locale query parameter from the URL without a page reload.
 * Called after the parameter has been consumed so it doesn't persist in the
 * address bar or get shared in copied links.
 */
export function clearSourceLocaleParam(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(SOURCE_LOCALE_PARAM)) return;

  url.searchParams.delete(SOURCE_LOCALE_PARAM);
  const cleanUrl = url.pathname + url.search + url.hash;
  history.replaceState(null, '', cleanUrl);
}

/**
 * Parses unsupported language strings from JSON script tag in the DOM
 * @returns {Object|null} Parsed strings or null if not found/invalid
 */
export function parseUnsupportedLanguageStrings(): UnsupportedLanguageStrings | null {
  return parseStringsFromScript(
    SELECTORS.unsupportedLanguageStrings,
    'Unsupported language',
  ) as UnsupportedLanguageStrings | null;
}
